// src/components/EstadoSuscripcion.jsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  Button,
  Stack,
  Skeleton,
  Divider,
  Avatar,
  Chip,
  Fade,
  Paper,
  useTheme
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaymentIcon from '@mui/icons-material/Payment';

const EstadoSuscripcion = () => {
  const { suscripcion, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (loading) {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          bgcolor: 'background.paper',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Skeleton variant="text" width="70%" height={24} />
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" width="30%" height={32} />
          </Box>
        </Stack>
      </Paper>
    );
  }

  // Estado INACTIVO
  if (!suscripcion || !suscripcion.plan || suscripcion.estado !== 'Activa') {
    return (
      <Fade in={true} timeout={800}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: '16px',
            backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.05)' : 'rgba(255, 193, 7, 0.08)',
            borderColor: 'warning.main',
            boxShadow: isDarkMode ? '0 2px 8px rgba(255, 193, 7, 0.1)' : '0 2px 8px rgba(255, 193, 7, 0.15)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: isDarkMode ? '0 4px 12px rgba(255, 193, 7, 0.15)' : '0 4px 12px rgba(255, 193, 7, 0.25)',
            }
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'warning.main',
                width: 48,
                height: 48,
                boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)'
              }}
            >
              <WarningAmberIcon />
            </Avatar>
            <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
              Suscripción Requerida
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
              Necesitas un plan activo para acceder a todas las funcionalidades
            </Typography>
            <Button
              size="medium"
              variant="contained"
              color="warning"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/doctor/planes')}
              sx={{
                borderRadius: '20px',
                py: 1,
                px: 3,
                boxShadow: isDarkMode ? '0 2px 5px rgba(255, 193, 7, 0.2)' : '0 2px 5px rgba(255, 193, 7, 0.3)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: isDarkMode ? '0 4px 8px rgba(255, 193, 7, 0.3)' : '0 4px 8px rgba(255, 193, 7, 0.4)',
                }
              }}
            >
              Ver Planes
            </Button>
          </Stack>
        </Paper>
      </Fade>
    );
  }

  // Estado ACTIVO
  return (
    <Fade in={true} timeout={800}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          borderColor: 'divider',
          '&:hover': {
            boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box
          sx={{
            bgcolor: 'success.main',
            color: 'success.contrastText',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold">
              Suscripción Activa
            </Typography>
          </Box>
          <Chip
            label={suscripcion.plan.nombre}
            size="small"
            sx={{
              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'white',
              color: 'success.dark',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              px: 2,
              height: 32,
              '& .MuiChip-label': {
                px: 1,
                py: 0.5,
                fontSize: '0.6rem',
                whiteSpace: 'normal',
                lineHeight: 1,
              }
            }}
          />
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarTodayIcon color="action" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Vence el:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="medium">
                {new Date(suscripcion.fecha_fin).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaymentIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Precio:
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: isDarkMode ? 'white' : 'primary.main'
                }}
              >
                ${parseFloat(suscripcion.plan.precio).toFixed(2)}
                {suscripcion.plan.duracion_dias === 365
                  ? '/año'
                  : suscripcion.plan.duracion_dias === 30
                  ? '/mes'
                  : suscripcion.plan.duracion_dias === 7
                  ? '/semana'
                  : ''}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/doctor/suscripcion')}
              sx={{
                mt: 1,
                borderRadius: '20px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateX(3px)',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(25, 118, 210, 0.04)',
                }
              }}
            >
              Gestionar Plan
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Fade>
  );
};

export default EstadoSuscripcion;
